import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Icon } from 'semantic-ui-react';
import { FieldArray, change, arrayPush } from 'redux-form';
import _ from 'lodash';

import { createToast, toastType } from 'Common/ToastHelper';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import {
  PreparationInstructionEnum as Fields,
  PreparationInstructionIngredientEnum as PreparationIngredients,
  IngredientFieldEnum as IngredientFields,
  RecipeFieldEnum as RecipeFields,
  PreparationInstructionIngredientEnum,
} from 'Common/FieldEnum';
import { EDIT_RECIPE_FORM_NAME } from 'Common/FormEnum';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import {
  getHACCPTypesMap,
  getHACCPTypeOptions,
  getHACCPsMap,
  getFilteredHACCPOptions,
  getInstructionDescriptionOptions,
} from 'Pages/managemenu/state/common/selectors';
import {
  getIngredients,
  getIngredientsMap,
  getAvailableSteps,
  getPreparationInstructions,
  getIsReadOnly,
} from 'Pages/managerecipe/state/editrecipe/selectors';

import { PreparationInstruction } from './PreparationInstruction';
import { IngredientsModal } from './IngredientsModal';
import { HACCPModal } from './HACCPModal';
import { ADD_INGREDIENT_HEADER } from '../../helpers/constants';

const renderInstructions = ({
  fields,
  data,
  ingredients,
  ingredientsMap,
  haccpsMap,
  haccpTypesMap,
  availableSteps,
  handleOpenIngredientModalClick,
  handleOpenHACCPModalClick,
  instructionDescriptionOptions,
  handleAddIngredientToDescriptionClick,
  handleConfirmRemoveHACCPInstruction,
  handleConfirmRemoveIngredient,
  handleConfirmRemoveStep,
  isReadOnly,
}) => {
  return fields.map((instruction) => {
    return (
      <PreparationInstruction
        key={instruction}
        data={data}
        instruction={instruction}
        ingredients={ingredients}
        ingredientsMap={ingredientsMap}
        haccpsMap={haccpsMap}
        haccpTypesMap={haccpTypesMap}
        availableSteps={availableSteps}
        instructionDescriptionOptions={instructionDescriptionOptions}
        handleOpenIngredientModalClick={handleOpenIngredientModalClick}
        handleOpenHACCPModalClick={handleOpenHACCPModalClick}
        handleAddIngredientToDescriptionClick={
          handleAddIngredientToDescriptionClick
        }
        handleConfirmRemoveHACCPInstruction={
          handleConfirmRemoveHACCPInstruction
        }
        handleConfirmRemoveIngredient={handleConfirmRemoveIngredient}
        handleConfirmRemoveStep={handleConfirmRemoveStep}
        isReadOnly={isReadOnly}
      />
    );
  });
};

class PreparationInstructions extends Component {
  state = {
    showIngredientModal: false,
    showHACCPModal: false,
    instruction: null,
  };

  static propTypes = {
    data: PropTypes.object.isRequired,
  };

  get stepsAreAvailable() {
    const { preparationInstructions } = this.props;
    if (preparationInstructions.length >= 10) {
      return false;
    }
    return true;
  }

  handleOpenIngredientModalClick = (instruction) => {
    this.setState({ instruction: instruction, showIngredientModal: true });
  };

  handleOpenHACCPModalClick = (instruction) => {
    this.setState({ instruction: instruction, showHACCPModal: true });
  };

  handleAddStep = () => {
    const { arrayPush, availableSteps } = this.props;
    if (!this.stepsAreAvailable) {
      return;
    }
    const nextAvailableSteps = availableSteps
      .filter((x) => !x.disabled)
      .map((x) => x.value);
    const step = Math.min(...nextAvailableSteps);
    arrayPush(EDIT_RECIPE_FORM_NAME, RecipeFields.PreparationInstructions, {
      Id: '',
      Step: step,
      DescriptionId: 3,
      Ingredients: [],
      Directions: '',
      HACCPInstructions: [],
    });
  };

  handleAddIngredientToDescriptionClick = (ingredient, instruction) => {
    const { data, change } = this.props;
    let directions = _.get(data, `${instruction}.${Fields.Directions}`);
    const newLine = directions ? '\n' : '';
    directions += newLine + ingredient[IngredientFields.IngredientName];
    change(
      EDIT_RECIPE_FORM_NAME,
      `${instruction}.${Fields.Directions}`,
      directions
    );
  };

  handleRemoveHACCPInstruction = (haccpInstructionId, instruction) => {
    const { data, change } = this.props;
    let newHACCPInstructions = _.get(
      data,
      `${instruction}.${Fields.HACCPInstructions}`
    );
    newHACCPInstructions = newHACCPInstructions.filter(
      (i) => i[Fields.Id] !== haccpInstructionId
    );
    change(
      EDIT_RECIPE_FORM_NAME,
      `${instruction}.${Fields.HACCPInstructions}`,
      newHACCPInstructions
    );
  };

  handleRemoveIngredient = (id, index, instruction) => {
    const { data, change } = this.props;
    let instructionIngredients = _.get(
      data,
      `${instruction}.${Fields.Ingredients}`
    );
    instructionIngredients = instructionIngredients.filter(
      (instructionIng) =>
        instructionIng[PreparationInstructionIngredientEnum.Id] !== id ||
        instructionIng[PreparationInstructionIngredientEnum.Index] !== index
    );
    change(
      EDIT_RECIPE_FORM_NAME,
      `${instruction}.${Fields.Ingredients}`,
      instructionIngredients
    );
  };

  handleRemoveStepClick = (instruction) => {
    const { data, change } = this.props;
    const currentStep = _.get(data, `${instruction}.${Fields.Step}`);
    let newInstructions = _.get(data, RecipeFields.PreparationInstructions);
    newInstructions = newInstructions.filter(
      (i) => i[Fields.Step] !== currentStep
    );
    change(
      EDIT_RECIPE_FORM_NAME,
      RecipeFields.PreparationInstructions,
      newInstructions
    );
  };

  handleConfirmRemoveHACCPInstruction = (haccpInstructionId, instruction) => {
    const { setNotificationDialog } = this.props;
    setNotificationDialog({
      ...NOTIFICATION_DIALOG_MESSAGES.REMOVE_HACCP_DIRECTION,
      functionsToRunOnOk: [
        () =>
          this.handleRemoveHACCPInstruction(haccpInstructionId, instruction),
      ],
    });
  };

  handleConfirmRemoveIngredient = (id, ingIndex, instruction) => {
    const { setNotificationDialog } = this.props;
    setNotificationDialog({
      ...NOTIFICATION_DIALOG_MESSAGES.REMOVE_INGREDIENT,
      functionsToRunOnOk: [
        () => this.handleRemoveIngredient(id, ingIndex, instruction),
      ],
    });
  };

  handleConfirmRemoveStep = (instruction) => {
    const { setNotificationDialog } = this.props;
    setNotificationDialog({
      ...NOTIFICATION_DIALOG_MESSAGES.REMOVE_PREPARATION_STEP,
      functionsToRunOnOk: [() => this.handleRemoveStepClick(instruction)],
    });
  };

  handleAddIngredientClick = (id, index, instruction) => {
    const { arrayPush } = this.props;
    arrayPush(EDIT_RECIPE_FORM_NAME, `${instruction}.${Fields.Ingredients}`, {
      [PreparationIngredients.Id]: id,
      [PreparationIngredients.Index]: index,
    });
    createToast(toastType.success, 'Success', 'Added Ingredient!', true);
  };

  handleAddHACCPClicked = (instruction) => {
    const { data, arrayPush } = this.props;
    arrayPush(
      EDIT_RECIPE_FORM_NAME,
      `${instruction}.${Fields.HACCPInstructions}`,
      {
        [Fields.Id]: _.uniqueId(),
        [Fields.Position]: data[Fields.Position],
        [Fields.TypeId]: data[Fields.TypeId],
        [Fields.HACCPCriticalLimitId]: data[Fields.HACCPCriticalLimitId],
        [Fields.HACCPNotes]: data[Fields.HACCPNotes],
      }
    );
    this.handleClearFormFields();
    this.setState({ showHACCPModal: false });
  };

  handleClearFormFields = () => {
    const { change } = this.props;
    change(EDIT_RECIPE_FORM_NAME, [Fields.Position], null);
    change(EDIT_RECIPE_FORM_NAME, [Fields.TypeId], null);
    change(EDIT_RECIPE_FORM_NAME, [Fields.HACCPCriticalLimitId], null);
    change(EDIT_RECIPE_FORM_NAME, [Fields.HACCPNotes], '');
  };

  render() {
    const {
      data,
      haccpsMap,
      haccpOptions,
      haccpTypesMap,
      haccpTypeOptions,
      ingredients,
      ingredientsMap,
      availableSteps,
      instructionDescriptionOptions,
      isReadOnly,
    } = this.props;
    const { showIngredientModal, showHACCPModal, instruction } = this.state;

    return (
      <React.Fragment>
        <FieldArray
          name={RecipeFields.PreparationInstructions}
          component={renderInstructions}
          data={data}
          ingredients={ingredients}
          ingredientsMap={ingredientsMap}
          haccpsMap={haccpsMap}
          haccpTypesMap={haccpTypesMap}
          availableSteps={availableSteps}
          instructionDescriptionOptions={instructionDescriptionOptions}
          handleOpenIngredientModalClick={this.handleOpenIngredientModalClick}
          handleOpenHACCPModalClick={this.handleOpenHACCPModalClick}
          handleAddIngredientToDescriptionClick={
            this.handleAddIngredientToDescriptionClick
          }
          handleConfirmRemoveHACCPInstruction={
            this.handleConfirmRemoveHACCPInstruction
          }
          handleConfirmRemoveIngredient={this.handleConfirmRemoveIngredient}
          handleConfirmRemoveStep={this.handleConfirmRemoveStep}
          isReadOnly={isReadOnly}
        />
        <Button
          style={{ margin: 10 }}
          onClick={this.handleAddStep}
          disabled={!this.stepsAreAvailable || isReadOnly}
        >
          <Icon name="plus" />
          Add Step
        </Button>
        <IngredientsModal
          headerTitle={ADD_INGREDIENT_HEADER}
          modalProps={{
            open: showIngredientModal,
            onClose: () => {
              this.setState({ showIngredientModal: false });
            },
            size: 'large',
          }}
          ingredients={ingredients}
          instruction={instruction}
          handleAddIngredientClick={this.handleAddIngredientClick}
        />
        <HACCPModal
          headerTitle={'Add HACCP Instructions'}
          modalProps={{
            open: showHACCPModal,
            onClose: () => {
              this.setState({ showHACCPModal: false });
            },
            size: 'small',
          }}
          instruction={instruction}
          data={data}
          haccpOptions={haccpOptions}
          haccpTypeOptions={haccpTypeOptions}
          handleAddHACCPClicked={this.handleAddHACCPClicked}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    haccpsMap: getHACCPsMap(state),
    haccpOptions: getFilteredHACCPOptions(state, EDIT_RECIPE_FORM_NAME),
    haccpTypesMap: getHACCPTypesMap(state),
    haccpTypeOptions: getHACCPTypeOptions(state),
    ingredients: getIngredients(state),
    ingredientsMap: getIngredientsMap(state),
    availableSteps: getAvailableSteps(state),
    instructionDescriptionOptions: getInstructionDescriptionOptions(state),
    preparationInstructions: getPreparationInstructions(state),
    isReadOnly: getIsReadOnly(state),
  };
};

const mapDispatchToProps = {
  arrayPush,
  change,
  setNotificationDialog,
};

PreparationInstructions = connect(
  mapStateToProps,
  mapDispatchToProps
)(PreparationInstructions);

export { PreparationInstructions };



// WEBPACK FOOTER //
// ./src/pages/managerecipe/components/PreparationInstructions/PreparationInstructions.js